/**
 * Thumbs 👍/👎 de una corrección (plan 26 §7.4, F3b) — port del `LpThumbs` del
 * bundle de diseño debrief-loop. Voto CONSULTIVO: el copy nunca promete que la
 * corrección cambie ("no borra la corrección — afina las próximas"). Toggle
 * sin fricción; con 👎 aparecen chips de motivo opcionales que separan las dos
 * fuentes de falso positivo (STT vs LLM).
 *
 * Optimista: pinta el voto al tap y lo persiste vía `PATCH
 * /corrections/feedback` (clave natural — el WS en vivo no trae id de fila).
 * Si el PATCH falla, revierte y loggea (sin toast: votar es secundario, no
 * puede interrumpir la lectura de la corrección).
 */
import { useState } from 'react';
import {
  sendCorrectionFeedback,
  type CorrectionFeedbackReason,
  type CorrectionFeedbackVote,
} from '../../../lib/englishApi.js';
import './correctionThumbs.css';

const REASON_LABELS: ReadonlyArray<{ id: CorrectionFeedbackReason; label: string }> = [
  { id: 'not_said', label: 'No dije eso' },
  { id: 'was_fine', label: 'Mi frase estaba bien' },
  { id: 'dont_understand', label: 'No la entiendo' },
];

function ThumbIcon({ down = false, size = 15 }: { down?: boolean; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* El 👎 se espeja en la GEOMETRÍA, no con un `transform` CSS sobre el
          <svg>: el botón votado corre `cthNod`, y una animación gana en la
          cascada incluso contra un style inline — el pulgar volvía a apuntar
          para arriba (y con `both` se quedaba así) apenas votabas 👎. */}
      <g transform={down ? 'translate(0 24) scale(1 -1)' : undefined}>
        <path d="M7 11 L 11 3.5 a 2.2 2.2 0 0 1 2 3.5 L 12.2 9 H 18 a 2 2 0 0 1 1.9 2.6 l -2 6.5 A 2 2 0 0 1 16 19.5 H 7" />
        <path d="M7 10.5 H 4 V 20 H 7 Z" />
      </g>
    </svg>
  );
}

export interface CorrectionThumbsProps {
  sessionId: string;
  /** `null` = la fila se persistió sin turno (parte de la clave natural). */
  turnIndex: number | null;
  said: string;
  fix: string;
  /** Voto previo (rehidratado del transcript/summary). */
  initialFeedback?: { vote: 'up' | 'down'; reason?: string } | undefined;
}

export function CorrectionThumbs({
  sessionId,
  turnIndex,
  said,
  fix,
  initialFeedback,
}: CorrectionThumbsProps) {
  const [vote, setVote] = useState<CorrectionFeedbackVote | null>(initialFeedback?.vote ?? null);
  const [reason, setReason] = useState<CorrectionFeedbackReason | null>(
    initialFeedback?.reason === 'not_said' ||
      initialFeedback?.reason === 'was_fine' ||
      initialFeedback?.reason === 'dont_understand'
      ? initialFeedback.reason
      : null,
  );

  const persist = (
    nextVote: CorrectionFeedbackVote | null,
    nextReason: CorrectionFeedbackReason | null,
    revert: () => void,
  ) => {
    sendCorrectionFeedback({
      sessionId,
      turnIndex,
      said,
      fix,
      vote: nextVote,
      ...(nextVote === 'down' && nextReason ? { reason: nextReason } : {}),
    }).catch((err: unknown) => {
      console.warn('[correction-thumbs] feedback failed', err);
      revert();
    });
  };

  const cast = (v: CorrectionFeedbackVote) => {
    const prevVote = vote;
    const prevReason = reason;
    const next = vote === v ? null : v; // toggle sin fricción
    setVote(next);
    setReason(null);
    persist(next, null, () => {
      setVote(prevVote);
      setReason(prevReason);
    });
  };

  const pickReason = (r: CorrectionFeedbackReason) => {
    const prevReason = reason;
    const next = reason === r ? null : r;
    setReason(next);
    persist('down', next, () => setReason(prevReason));
  };

  return (
    <div className="cth">
      <div className="cth-row">
        <span className="cth-q">
          {vote ? 'Gracias — esto nos ayuda a calibrar.' : '¿Te sirvió esta corrección?'}
        </span>
        <button
          type="button"
          className={`cth-btn up${vote === 'up' ? ' is-on' : ''}`}
          aria-label="Esta corrección está bien"
          aria-pressed={vote === 'up'}
          onClick={() => cast('up')}
        >
          <ThumbIcon />
        </button>
        <button
          type="button"
          className={`cth-btn down${vote === 'down' ? ' is-on' : ''}`}
          aria-label="Esta corrección no está bien"
          aria-pressed={vote === 'down'}
          onClick={() => cast('down')}
        >
          <ThumbIcon down />
        </button>
      </div>
      {vote === 'down' && (
        <div className="cth-chips" role="group" aria-label="Motivo (opcional)">
          {REASON_LABELS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`cth-chip${reason === r.id ? ' is-on' : ''}`}
              aria-pressed={reason === r.id}
              onClick={() => pickReason(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      )}
      {vote === 'down' && reason && (
        <span className="cth-thanks">Anotado. No borra la corrección — afina las próximas.</span>
      )}
    </div>
  );
}
